import { useState } from "react";
import {
  Box, Button, Stack, Typography, IconButton,
  Tabs, Tab, TextField, InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useNavigate } from "react-router-dom";

import PreviousClosuresList from "../components/PreviousClosuresList";
import { usePayrollClosures } from "../hooks/useLiquidacionData";


function Pill({ children, sx }) {
  return (
    <Box sx={{ px: 2.5, py: 1.1, bgcolor: "secondary.50", borderRadius: "33px", boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)", ...sx }}>
      {children}
    </Box>
  );
}
function ClearableTextField({ value, onChange, onClear, startAdornment, ...rest }) {
  return (
    <TextField
      {...rest}
      value={value}
      onChange={onChange}
      InputProps={{
        startAdornment: startAdornment && !value ? (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ) : undefined,
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={onClear}
              edge="end"
              aria-label="Limpiar filtro"
              sx={{ color: "neutral.500", "&:hover": { color: "primary.900" } }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
}
export default function LiquidacionPage() {
  const navigate = useNavigate();

  const [tabIndex, setTabIndex] = useState(0);
  const [searchName, setSearchName] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [page, setPage] = useState(1);
  const limit = 10;

  const currentStatus = tabIndex === 0 ? 'READY' : 'DRAFT';

  const { closures, totalItems, loading, error } = usePayrollClosures({
    status: currentStatus,
    name: searchName,
    periodStart: filterStart,
    periodEnd: filterEnd,
    page,
    limit
  });

  const totalPages = Math.max(1, Math.ceil(totalItems / limit));

  const handleViewDatos = (closureId) => navigate(`/rrhh/liquidacion/${closureId}/datos`);
  const handleViewResumen = (closureId) => navigate(`/rrhh/liquidacion/${closureId}/reporte`);
  
  const handleResumeClosure = (closureId, step) => {
    if (step === 2) navigate(`/rrhh/liquidacion/${closureId}/usuarios`);
    else if (step === 3) navigate(`/rrhh/liquidacion/${closureId}/datos`);
    else if (step === 4) navigate(`/rrhh/liquidacion/${closureId}/accounting`);
    else if (step === 5) navigate(`/rrhh/liquidacion/${closureId}/reporte`);
    else navigate(`/rrhh/liquidacion/${closureId}/usuarios`); // Fallback seguro
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    setPage(1);
  };

  const clearSearch    = () => { setSearchName('');  setPage(1); };
  const clearStart     = () => { setFilterStart(''); setPage(1); };
  const clearEnd       = () => { setFilterEnd('');   setPage(1); };

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <IconButton onClick={() => navigate("/rrhh/dashboard")} sx={{ width: 36, height: 36, bgcolor: "secondary.500", "&:hover": { bgcolor: "secondary.600" } }}>
          <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
        </IconButton>
        <Stack sx={{ flex: 1, alignItems: "center", pr: "48px" }}>
          <Pill sx={{ bgcolor: "secondary.200" }}><Typography sx={{ fontSize: 18, fontWeight: 600, color: "primary.900" }}>Liquidaciones</Typography></Pill>
        </Stack>
      </Stack>

      {/* Botones Superiores */}
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mt: 1 }}>
        <Button
          onClick={() => navigate('/rrhh/liquidacion/faltantes')}
          sx={{ bgcolor: "#ffcdd2", color: "primary.900", px: 2.5, py: 1, borderRadius: "24px", border: "2px solid", textTransform: "none", fontWeight: 700, fontSize: 15, "&:hover": { bgcolor: "#ffb3ba" } }}
        >
          Cierres faltantes
        </Button>
        <Button
          onClick={() => navigate("/rrhh/liquidacion/crear")}
          sx={{ bgcolor: "#b9f6ca", color: "primary.900", px: 2.5, py: 1, borderRadius: "24px", border: "2px solid", borderColor: "primary.900", textTransform: "none", fontWeight: 700, fontSize: 15, "&:hover": { bgcolor: "#a5f5bd" } }}
        >
          Generar cierre
        </Button>
      </Stack>

      {/* Contenedor Principal (Filtros + Lista) */}
      <Box sx={{ bgcolor: "white", borderRadius: "24px", p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        
        {/* Pestañas (Tabs) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            sx={{ flex: 1, minWidth: 0, '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', fontSize: 15 } }}
          >
            <Tab label="Cierres completados" />
            <Tab label="Cierres en curso" />
          </Tabs>
          <Button
            onClick={() => navigate('/rrhh/liquidacion/reporte-pagos')}
            startIcon={<AssessmentIcon />}
            sx={{
              flexShrink: 0,
              bgcolor: "primary.900",
              color: "white",
              px: 2.5,
              py: 0.8,
              mb: 0.5,
              borderRadius: "24px",
              border: "2px solid",
              borderColor: "primary.900",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              "&:hover": { bgcolor: "primary.800" },
            }}
          >
            Reporte de pagos
          </Button>
        </Box>

        {/* Barra de Filtros */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <ClearableTextField
            size="small"
            placeholder="Buscar por nombre..."
            value={searchName}
            onChange={(e) => { setSearchName(e.target.value); setPage(1); }}
            onClear={clearSearch}
            startAdornment={<SearchIcon sx={{ color: 'neutral.500' }} />}
            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '16px', bgcolor: 'white' } }}
          />
          <ClearableTextField
            size="small"
            label="Desde"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filterStart}
            onChange={(e) => { setFilterStart(e.target.value); setPage(1); }}
            onClear={clearStart}
            sx={{ width: { xs: '100%', md: '200px' }, '& .MuiOutlinedInput-root': { borderRadius: '16px', bgcolor: 'white' } }}
          />
          <ClearableTextField
            size="small"
            label="Hasta"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filterEnd}
            onChange={(e) => { setFilterEnd(e.target.value); setPage(1); }}
            onClear={clearEnd}
            sx={{ width: { xs: '100%', md: '200px' }, '& .MuiOutlinedInput-root': { borderRadius: '16px', bgcolor: 'white' } }}
          />
        </Stack>

        {/* Lista de Cierres */}
        <PreviousClosuresList
          closures={closures}
          loading={loading}
          error={error}
          onViewDatos={handleViewDatos}
          onViewResumen={handleViewResumen}
          onResume={handleResumeClosure}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </Box>
    </Stack>
  );
}
